import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../components/UI/States";
import { BackLink, BranchSelect, Notice, SearchField } from "../../components/Fleet/FleetParts";
import { formatDate } from "../../components/Fleet/fleetFormat";
import { fetchTraining } from "./safetyApi";
import {
  AllClear,
  Chip,
  ListCard,
  ProgressBar,
  UploadVideoDialog,
  matchesQuery,
  overdueText,
  percent,
  pickBranch,
  plural,
  sameBranch,
  useUrlFilters,
} from "./SafetyParts";
import { SAFETY_PAGES } from "./safetyPages";
import { ReportActions, reportScope } from "./SafetyReport";

// Training videos: upload, see progress per video, and who's overdue. Click
// a video to assign it (TrainingVideoDetail). Quizzes are built in the app
// (Safety Manager → Training Videos).

const PAGE = SAFETY_PAGES.training;

const TrainingVideos = () => {
  const user = useSelector(selectUser);
  const filters = useUrlFilters();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(() => {
    if (!user?.id) return;
    fetchTraining(user.id)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [user?.id]);

  useEffect(() => load(), [load]);

  const assignments = data?.assignments;
  const branches = useMemo(
    () => [...new Set((assignments || []).map((a) => a.branch))].sort((a, b) => a.localeCompare(b)),
    [assignments]
  );
  const mine = (assignments || []).filter((a) => !filters.branch || sameBranch(a.branch, filters.branch));
  const done = mine.filter((a) => a.completed).length;
  const overdue = mine
    .filter((a) => a.overdue)
    .filter((a) => matchesQuery(filters.query, a.name, a.email, a.videoTitle, a.branch))
    .sort((a, b) => String(a.dueAt).localeCompare(String(b.dueAt)));
  const videos = (data?.videos || [])
    .map((v) => {
      const list = mine.filter((a) => a.videoId === v.id);
      return {
        ...v,
        assigned: list.length,
        completed: list.filter((a) => a.completed).length,
        overdue: list.filter((a) => a.overdue).length,
      };
    })
    // A branch view shows only what's assigned there; company-wide keeps
    // unassigned videos (a fresh upload) so they can be assigned.
    .filter((v) => v.assigned > 0 || !filters.branch);

  return (
    <PageShell size="xl">
      <UploadVideoDialog kind={uploading} userId={user?.id} onClose={() => setUploading(null)} onUploaded={load} />
      <BackLink to="/safety/overview">Safety Overview</BackLink>
      <PageHeader
        icon={PAGE.icon}
        tone={PAGE.tone}
        eyebrow="Safety"
        title={PAGE.name}
        description="Videos assigned to people and roles, with due dates. People watch them in the Big C mobile app."
        actions={
          <button type="button" className="app-btn app-btn-primary" onClick={() => setUploading("training")}>
            <CloudUploadRoundedIcon sx={{ fontSize: 18 }} /> Upload training video
          </button>
        }
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!data && !error && <LoadingState label="Loading training…" />}

      {data && (
        <>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <BranchSelect
              value={pickBranch(branches, filters.branch)}
              onChange={(v) => filters.set("branch", v)}
              branches={branches}
            />
            <ReportActions
              kind="training"
              userId={user?.id}
              tone={PAGE.tone}
              className="sm:ml-auto"
              disabled={!videos.length}
              selection={{
                branch: pickBranch(branches, filters.branch),
                videoIds: videos.map((v) => v.id),
                ids: overdue.map((a) => a.id),
                scope: reportScope({
                  branch: pickBranch(branches, filters.branch),
                  status: filters.query.trim() && `Overdue list matching “${filters.query.trim()}”`,
                }),
              }}
            />
          </div>

          <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile label="Assignments" value={mine.length} hint={plural(data.videos.length, "video")} />
            <StatTile
              label="Done"
              value={`${percent(done, mine.length)}%`}
              hint={`${done} of ${mine.length}`}
              tone={mine.length && done === mine.length ? "emerald" : "default"}
            />
            <StatTile
              label="Overdue"
              value={mine.filter((a) => a.overdue).length}
              hint="Past the due date"
              tone={mine.some((a) => a.overdue) ? "rose" : "default"}
            />
            <StatTile
              label="Due this week"
              value={mine.filter((a) => a.dueSoon).length}
              hint="Not done yet"
              tone={mine.some((a) => a.dueSoon) ? "amber" : "default"}
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            <ListCard title="Videos" description="Click a video to assign it or see who's done." className="lg:col-span-3">
              {videos.length === 0 ? (
                <EmptyState
                  compact
                  icon={PAGE.icon}
                  title={filters.branch ? "Nothing assigned at this branch." : "No training videos yet."}
                  description={filters.branch ? undefined : "Upload one to get started."}
                />
              ) : (
                <ul className="mt-3 divide-y divide-[color:var(--app-divider)] border-t app-divider">
                  {videos.map((video) => (
                    <li key={video.id}>
                      <Link
                        to={`${PAGE.path}/${video.id}`}
                        className="flex items-start gap-3 px-4 py-3 transition hover:bg-[color:var(--app-hover)] sm:px-5"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{video.title}</span>
                            {video.quizEnabled && <Chip tone="violet">Quiz</Chip>}
                          </span>
                          <span className="app-muted block text-xs">
                            Uploaded {formatDate(video.createdAt)}
                            {video.addedByName ? ` by ${video.addedByName}` : ""}
                            {video.roleRules.length > 0 &&
                              ` · auto-assigned to ${video.roleRules.map((r) => r.role).join(", ")}`}
                          </span>
                          {video.assigned === 0 ? (
                            <span className="mt-1 block text-sm text-amber-700 dark:text-amber-300">
                              Not assigned yet — nobody can see it until it's assigned.
                            </span>
                          ) : (
                            <>
                              <span className="mt-2 block">
                                <ProgressBar completed={video.completed} overdue={video.overdue} total={video.assigned} />
                              </span>
                              <span className="mt-1 block text-xs tabular-nums">
                                {video.completed} of {video.assigned} done ({percent(video.completed, video.assigned)}%)
                                {video.overdue > 0 && (
                                  <span className="font-semibold text-rose-700 dark:text-rose-300"> · {video.overdue} overdue</span>
                                )}
                              </span>
                            </>
                          )}
                        </span>
                        <ChevronRightRoundedIcon className="app-muted mt-1 shrink-0" fontSize="small" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </ListCard>

            <ListCard
              title="Overdue"
              description="Past the due date and not done, most overdue first."
              className="lg:col-span-2"
            >
              <div className="px-4 pt-3 sm:px-5">
                <SearchField
                  value={filters.query}
                  onChange={(v) => filters.set("q", v)}
                  placeholder="Search name or video…"
                  className="w-full"
                />
              </div>
              {overdue.length === 0 ? (
                <AllClear>{filters.query ? "Nobody overdue matches that search." : "Nobody is overdue."}</AllClear>
              ) : (
                <ul className="mt-3 max-h-[32rem] divide-y divide-[color:var(--app-divider)] overflow-y-auto border-t app-divider">
                  {overdue.map((a) => (
                    <li key={a.id}>
                      <Link
                        to={`${PAGE.path}/${a.videoId}`}
                        className="flex items-start justify-between gap-3 px-4 py-2 text-sm transition hover:bg-[color:var(--app-hover)] sm:px-5"
                      >
                        <span className="min-w-0">
                          <span className="font-semibold">{a.name || "Unknown"}</span>
                          <span className="app-muted"> · {a.branch}</span>
                          <span className="app-muted block truncate text-xs">{a.videoTitle}</span>
                        </span>
                        <span className="shrink-0 text-right text-xs">
                          <span className="block font-semibold text-rose-700 dark:text-rose-300">{overdueText(a.dueAt)}</span>
                          <span className="app-muted">Due {formatDate(a.dueAt)}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </ListCard>
          </div>
        </>
      )}
    </PageShell>
  );
};

export default TrainingVideos;
